import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAgreement, getAgreementApprovalsByApprover } from "../../../api/agreementsApi";
import { UserContext } from "../../../contexts/CurrentUserContext";
import Preloader from "../../Preloader/Preloader";
import {
  formatAgreementNumber,
  getApprovalStatusText,
  getStepStatusText,
  normalizeMyAgreementApprovalEntry,
  resolveAgreementCounterpartyName,
} from "./agreementApprovalHelpers";
import "./AgreementApprovalList.css";

const getStatusIcon = (status) => {
  if (status === "approved" || status === "signed") {
    return (
      <span className="agreement-approval-list__status-icon agreement-approval-list__status-icon--approved">
        ✓
      </span>
    );
  }
  if (status === "pending") {
    return (
      <span className="agreement-approval-list__status-icon agreement-approval-list__status-icon--pending">
        ⏳
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span className="agreement-approval-list__status-icon agreement-approval-list__status-icon--rejected">
        ✕
      </span>
    );
  }
  return null;
};

function AgreementApprovalList() {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState("all");
  const [selectedCounterparty, setSelectedCounterparty] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    let cancelled = false;

    const fetchItems = async () => {
      if (!userData?.id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await getAgreementApprovalsByApprover(userData.id);
        if (cancelled) return;
        const list = (Array.isArray(data) ? data : [])
          .map((entry) =>
            normalizeMyAgreementApprovalEntry(entry, userData.id),
          )
          .filter(Boolean);

        const enriched = await Promise.all(
          list.map(async (item) => {
            if (item.projectName && item.counterpartyName && item.price != null) {
              return item;
            }
            if (!item.agreementId) return item;
            try {
              const agreement = await getAgreement(item.agreementId);
              return {
                ...item,
                agreement,
                projectName: item.projectName ?? agreement?.project_name ?? null,
                counterpartyName:
                  item.counterpartyName ??
                  resolveAgreementCounterpartyName(agreement),
                agreementNumber: item.agreementNumber ?? agreement?.number,
                price: item.price ?? agreement?.price ?? null,
                projectId: item.projectId ?? agreement?.project_id ?? null,
              };
            } catch {
              return item;
            }
          }),
        );

        if (cancelled) return;
        setItems(enriched);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.message || "Ошибка при загрузке согласований соглашений",
          );
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchItems();
    return () => {
      cancelled = true;
    };
  }, [userData?.id]);

  const uniqueProjects = useMemo(
    () =>
      Array.from(
        new Set(items.map((item) => item.projectName).filter(Boolean)),
      ),
    [items],
  );

  const uniqueCounterparties = useMemo(
    () =>
      Array.from(
        new Set(items.map((item) => item.counterpartyName).filter(Boolean)),
      ).sort((a, b) => a.localeCompare(b, "ru")),
    [items],
  );

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (selectedProject !== "all" && item.projectName !== selectedProject) {
        return false;
      }
      if (
        selectedCounterparty !== "all" &&
        item.counterpartyName !== selectedCounterparty
      ) {
        return false;
      }
      if (selectedStatus !== "all") {
        const myStatus = item.myStep?.status ?? item.status;
        const normalizedStatus =
          myStatus === "approved" ? "signed" : myStatus;
        const filterStatus =
          selectedStatus === "approved" ? "signed" : selectedStatus;
        if (normalizedStatus !== filterStatus) return false;
      }
      return true;
    });
  }, [items, selectedProject, selectedCounterparty, selectedStatus]);

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [filteredItems, sortOrder]);

  const formatCurrency = (value) => {
    if (value == null) return "—";
    return new Intl.NumberFormat("ru-RU").format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (loading) return <Preloader />;

  if (error) {
    return <div className="agreement-approval-list__error">{error}</div>;
  }

  return (
    <>
      <div className="project-approval__filters">
        <div className="project-approval__filter">
          <label className="project-approval__filter-label">Выбор проекта</label>
          <select
            className="project-approval__filter-select"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
          >
            <option value="all">Все проекты</option>
            {uniqueProjects.map((project) => (
              <option key={project} value={project}>
                {project}
              </option>
            ))}
          </select>
        </div>

        <div className="project-approval__filter">
          <label className="project-approval__filter-label">Субподрядчик</label>
          <select
            className="project-approval__filter-select"
            value={selectedCounterparty}
            onChange={(e) => setSelectedCounterparty(e.target.value)}
          >
            <option value="all">Все</option>
            {uniqueCounterparties.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="project-approval__filter">
          <label className="project-approval__filter-label">Мой статус</label>
          <select
            className="project-approval__filter-select"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">Все</option>
            <option value="pending">На рассмотрении</option>
            <option value="signed">Согласован</option>
            <option value="rejected">Отклонён</option>
          </select>
        </div>
      </div>

      {sortedItems.length === 0 ? (
        <div className="agreement-approval-list__empty">
          Нет соглашений для согласования
        </div>
      ) : (
        <div className="agreement-approval-list__table-container">
          <table className="agreement-approval-list__table">
            <thead>
              <tr>
                <th>Проект</th>
                <th>Соглашение</th>
                <th>Субподрядчик</th>
                <th>Сумма</th>
                <th>Общий статус</th>
                <th>Мой статус</th>
                <th>
                  <div className="agreement-approval-list__sort-header">
                    Дата отправки
                    <button
                      type="button"
                      className="agreement-approval-list__sort-button"
                      onClick={() =>
                        setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
                      }
                    >
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item) => {
                const myStatus = item.myStep?.status ?? "pending";
                return (
                  <tr
                    key={item.id}
                    className="agreement-approval-list__table-row"
                    onClick={() =>
                      navigate(
                        `/approvals/project-approval/agreements/${item.agreementId}/${item.id}`,
                      )
                    }
                  >
                    <td>{item.projectName || "—"}</td>
                    <td>{formatAgreementNumber(item.agreementNumber)}</td>
                    <td>{item.counterpartyName || "—"}</td>
                    <td>
                      {item.price != null
                        ? `₸ ${formatCurrency(item.price)}`
                        : "—"}
                    </td>
                    <td>
                      <div className="agreement-approval-list__status-item">
                        {getStatusIcon(item.status)}
                        <span className="agreement-approval-list__status-text">
                          {getApprovalStatusText(item.status)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="agreement-approval-list__status-item">
                        {getStatusIcon(myStatus)}
                        <span className="agreement-approval-list__status-text">
                          {getStepStatusText(myStatus)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="agreement-approval-list__date">
                        <span className="agreement-approval-list__date-icon">
                          📅
                        </span>
                        {formatDate(item.createdAt)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default AgreementApprovalList;
